import { 
  AdMob, 
  BannerAdOptions, 
  BannerAdSize, 
  BannerAdPosition, 
  RewardAdOptions,
  RewardAdPluginEvents,
  AdMobRewardItem,
  AdOptions
} from '@capacitor-community/admob';
import { Capacitor } from '@capacitor/core';

// Google Test IDs
const GOOGLE_TEST_IDS = {
  banner: 'ca-app-pub-3940256099942544/6300978111',
  interstitial: 'ca-app-pub-3940256099942544/1033173712',
  rewarded: 'ca-app-pub-3940256099942544/5224354917',
};

// User Production IDs (Defaults provided)
let AD_IDS = {
  banner: 'ca-app-pub-4999656050364868/1813969680',
  interstitial: 'ca-app-pub-4999656050364868/2801403286',
  rewarded: 'ca-app-pub-4999656050364868/4600232357',
};

let isTestingMode = false; // Default to false for configured IDs

let isInitialized = false;

/**
 * AdMob Service to manage ads across the app
 */
export const AdMobService = {
  /**
   * Configures the AdMob service with custom IDs
   */
  configure(config: { 
    bannerId?: string, 
    interstitialId?: string, 
    rewardedId?: string,
    isTesting?: boolean 
  }) {
    if (config.bannerId) AD_IDS.banner = config.bannerId;
    if (config.interstitialId) AD_IDS.interstitial = config.interstitialId;
    if (config.rewardedId) AD_IDS.rewarded = config.rewardedId;
    if (config.isTesting !== undefined) isTestingMode = config.isTesting;
    console.log('AdMob Service Configured:', { isTesting: isTestingMode });
  },

  /**
   * Initializes the AdMob plugin
   */
  async initialize(): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
      console.log('AdMob skipped: Not a native platform');
      return;
    }

    if (isInitialized) {
      console.log('AdMob already initialized, skipping...');
      return;
    }

    try {
      const { status } = await AdMob.trackingAuthorizationStatus();
      console.log('Tracking Status:', status);

      await AdMob.initialize({
        testingDevices: [], // Add your test device ID here during development
        initializeForTesting: isTestingMode,
      });
      isInitialized = true;
      console.log('AdMob Initialized Successfully');
    } catch (error) {
      console.error('AdMob Initialization Error:', error);
    }
  },

  /**
   * Shows a Banner Ad at the bottom of the screen
   */
  async showBanner(): Promise<void> {
    if (!Capacitor.isNativePlatform()) return;

    const options: BannerAdOptions = {
      adId: isTestingMode ? GOOGLE_TEST_IDS.banner : AD_IDS.banner,
      adSize: BannerAdSize.BANNER,
      position: BannerAdPosition.BOTTOM_CENTER,
      margin: 0,
      isTesting: isTestingMode,
    };

    try {
      await AdMob.showBanner(options);
      console.log('Banner Ad Displayed');
    } catch (error) {
      console.error('Banner Ad Error:', error);
    }
  },

  /**
   * Hides the currently visible Banner Ad
   */
  async hideBanner(): Promise<void> {
    if (!Capacitor.isNativePlatform()) return;
    try {
      await AdMob.hideBanner();
      await AdMob.removeBanner();
    } catch (error) {
      console.error('Hide Banner Error:', error);
    }
  },

  /**
   * Loads and shows an Interstitial Ad
   */
  async showInterstitial(): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
      console.log('Interstitial Ad skipped: Not a native platform');
      return;
    }
    try {
      const options: AdOptions = {
        adId: isTestingMode ? GOOGLE_TEST_IDS.interstitial : AD_IDS.interstitial,
        isTesting: isTestingMode,
      };

      await AdMob.prepareInterstitial(options);
      await AdMob.showInterstitial();
      console.log('Interstitial Ad Shown');
    } catch (error) {
      console.error('Interstitial Ad Error:', error);
    }
  },

  /**
   * Rewards the user after watching a video
   */
  async showRewardedAd(onReward: (reward: AdMobRewardItem) => void): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
      console.log('Rewarded Ad skipped: Not a native platform');
      return;
    }
    try {
      // Listen for reward event
      const listener = await AdMob.addListener(RewardAdPluginEvents.Rewarded, (reward: AdMobRewardItem) => {
        console.log('User Rewarded:', reward);
        onReward(reward);
        listener.remove(); // Clean up listener after reward
      });

      const options: RewardAdOptions = {
        adId: isTestingMode ? GOOGLE_TEST_IDS.rewarded : AD_IDS.rewarded,
        isTesting: isTestingMode,
      };

      await AdMob.prepareRewardVideoAd(options);
      await AdMob.showRewardVideoAd();
    } catch (error) {
      console.error('Rewarded Ad Error:', error);
    }
  }
};
